import { CastMember } from '../../../../api/structs';

/**
 * The engine's `CastUpdated`, delivered to whichever cast panel is mounted.
 *
 * The event carries the whole cast, so nothing has to be re-read: a checkpoint
 * that discovers a character, a reviewer that anchors one, and this tab's own
 * edits all arrive as the same complete list. `runId` is the ROOT run the cast
 * belongs to — a panel for any other run ignores it.
 *
 * A module-level registry rather than a window event, for the same reason
 * `useChildRun` uses one: subscribers are plain functions with types, and a
 * test can publish without going through the DOM.
 */
export interface CastUpdate {
  runId: string;
  cast: CastMember[];
}

type CastUpdateListener = (update: CastUpdate) => void;

const listeners = new Set<CastUpdateListener>();

/** Subscribe to every cast update, whatever run it is for. Returns the unsubscribe. */
export const subscribeCastUpdates = (listener: CastUpdateListener): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const publishCastUpdate = (update: CastUpdate): void => {
  Array.from(listeners).forEach((listener) => listener(update));
};

/**
 * Reads a `CastUpdated` event body — the `data` object of the hub's envelope,
 * unwrapped by the dispatcher exactly as it unwraps every other engine event.
 *
 * Returns null for anything that does not name a run — an update nobody can
 * address is not one worth publishing.
 */
export const castUpdateFromEvent = (payload: Record<string, unknown>): CastUpdate | null => {
  const runId = typeof payload.run_id === 'string' ? payload.run_id.trim() : '';
  if (!runId) return null;
  return {
    runId,
    // An emptied cast is a real update (the last member was removed), so an
    // absent list means empty rather than "no news".
    cast: Array.isArray(payload.cast) ? (payload.cast as CastMember[]) : [],
  };
};
